/**
 * Le tableau de bord général d'Éclosion.
 *
 * La première des quatre parties, et la page d'arrivée d'un administrateur
 * (décidé par Éclosion le 27/09/2026) : les informations générales, et les
 * raccourcis vers les pages qui les traitent. Il dit en quelques secondes
 * l'état du jour — activités à venir, remplissage, demandes à traiter, signal
 * financier — et chaque alerte mène à la page qui sait la traiter.
 *
 * Aucun chiffre n'est inventé : chaque bloc lit la base, et dit « illisible »
 * plutôt que zéro quand sa lecture échoue. Le signal financier n'est pas la
 * comptabilité : le détail est dans l'espace Comptabilité.
 */

import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Banknote, CalendarDays, Inbox, Landmark, Link2,
} from 'lucide-react';
import clsx from 'clsx';
import { CHEMINS } from '../../lib/navigation/chemins';
import { PARTIES } from '../../lib/navigation/espaces';
import {
  alertes as calculerLesAlertes, aujourdhuiABruxelles, remplissage, taux, SEUIL_PRESQUE_COMPLETE,
} from '../../lib/tableauDeBord/general';
import {
  compterLesMouvementsATraiter, lireLeDernierImport, lireLesDemandes,
  lireLesDerniersPaiements, lireLesSessionsAVenir,
} from '../../lib/tableauDeBord/general.supabase';
import { compterLesPiecesAComptabiliser } from '../../lib/tableauDeBord/comptabilite.supabase';
import { dateCourte, euros } from './components/money';
import { Bloc, Chiffre } from './tableauDeBord/Bloc';
import { useLecture, valeurOuNull } from './tableauDeBord/lecture';

const PROCHAINES = 8;

const SOURCES: Record<string, string> = { bank: 'CODA', bank_csv: 'CSV', stripe: 'Stripe' };

/** Les pages qu'on ouvre le plus souvent depuis l'administration. */
const RACCOURCIS = [
  { libelle: 'Utilisateurs', chemin: CHEMINS.utilisateurs.accueil },
  { libelle: 'Factures et notes de crédit', chemin: CHEMINS.comptabilite.factures },
  { libelle: 'Rapprochement bancaire', chemin: CHEMINS.comptabilite.rapprochement },
  { libelle: 'Programmation', chemin: CHEMINS.planification.programmation },
  { libelle: 'Inscriptions admin', chemin: CHEMINS.planification.inscriptions },
  { libelle: 'Stages', chemin: CHEMINS.planification.stages },
  { libelle: 'Demandes d’annulation', chemin: CHEMINS.planification.annulations },
  { libelle: 'Liste d’attente', chemin: CHEMINS.planification.listeAttente },
] as const;

export default function AdminDashboardPage() {
  const aujourdhui = useMemo(() => aujourdhuiABruxelles(), []);

  const [sessions] = useLecture(useCallback(() => lireLesSessionsAVenir(aujourdhui), [aujourdhui]));
  const [demandes] = useLecture(useCallback(() => lireLesDemandes(aujourdhui), [aujourdhui]));
  const [dernierImport] = useLecture(lireLeDernierImport);
  const [aTraiter] = useLecture(useCallback(() => compterLesMouvementsATraiter().then((r) => r.nombre), []));
  const [paiements] = useLecture(useCallback(() => lireLesDerniersPaiements(5), []));
  const [pieces] = useLecture(compterLesPiecesAComptabiliser);

  const alertes = calculerLesAlertes(
    {
      sessions: valeurOuNull(sessions)?.sessions ?? null,
      demandes: valeurOuNull(demandes),
      dernierImport: dernierImport.etat === 'pret' ? dernierImport.valeur : undefined,
      mouvementsATraiter: valeurOuNull(aTraiter),
      piecesAComptabiliser: valeurOuNull(pieces),
    },
    aujourdhui,
  );
  const toutLu = [sessions, demandes, dernierImport, aTraiter, pieces].every((l) => l.etat !== 'chargement');

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Tableau de bord</h1>
        <p className="text-gray-600">L’état du jour, au {dateCourte(aujourdhui)}.</p>
      </div>

      {/* --- Les trois autres parties, et les raccourcis ----------------- */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {PARTIES.filter((p) => p.chemin !== CHEMINS.tableauDeBord).map((p) => (
          <Link key={p.chemin} to={p.chemin}
                className="rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
            <p className="flex items-center gap-2 font-medium text-gray-900">
              <p.icone className="h-5 w-5" />{p.libelle}
            </p>
            <p className="mt-1 text-sm text-gray-600">{p.detail}</p>
          </Link>
        ))}
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
          <Link2 className="h-4 w-4" /> Raccourcis
        </h2>
        <ul className="flex flex-wrap gap-2">
          {RACCOURCIS.map((r) => (
            <li key={r.chemin}>
              <Link to={r.chemin}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50">
                {r.libelle} <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* --- Les alertes ------------------------------------------------ */}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
          <AlertTriangle className="h-4 w-4" /> À traiter
        </h2>
        {alertes.length === 0 ? (
          <p className="text-sm text-gray-500">
            {toutLu ? 'Aucune alerte sur ce que ce tableau sait lire.' : 'Lecture…'}
          </p>
        ) : (
          <ul className="space-y-2">
            {alertes.map((a) => (
              <li key={a.message}>
                <Link
                  to={a.lien}
                  className={clsx(
                    'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm hover:shadow-sm',
                    a.niveau === 'alerte'
                      ? 'border-red-200 bg-red-50 text-red-900'
                      : 'border-amber-200 bg-amber-50 text-amber-900',
                  )}
                >
                  <span>{a.message}</span>
                  <span className="shrink-0 text-xs underline">Traiter</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* --- Activités à venir --------------------------------------- */}
        <Bloc
          titre="Activités à venir"
          icone={<CalendarDays className="h-4 w-4" />}
          lecture={sessions}
          lien={{ to: CHEMINS.planification.programmation, libelle: 'Programmation' }}
        >
          {({ sessions: liste, tronque }) => {
            if (liste.length === 0) return <p className="text-sm text-gray-500">Aucune session à venir.</p>;
            const r = remplissage(liste, aujourdhui);
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Chiffre valeur={r.sessions} libelle="sessions à venir" />
                  <Chiffre
                    valeur={r.capacite > 0 ? `${Math.round((r.inscrits / r.capacite) * 100)} %` : '—'}
                    libelle={`remplies (${r.inscrits} / ${r.capacite})`}
                  />
                  <Chiffre valeur={r.placesRestantes} libelle="places restantes" />
                </div>
                <p className="text-xs text-gray-500">
                  {r.completes} complète{r.completes > 1 ? 's' : ''} ·{' '}
                  {r.presqueCompletes} à {Math.round(SEUIL_PRESQUE_COMPLETE * 100)} % ou plus ·{' '}
                  {r.faibles} faible{r.faibles > 1 ? 's' : ''} à moins de trois semaines
                  {tronque && ' · au-delà de 1 000 sessions, le compte est partiel'}
                </p>
                <ul className="divide-y divide-gray-100 text-sm">
                  {liste.slice(0, PROCHAINES).map((s) => {
                    const t = taux(s);
                    return (
                      <li key={s.id} className="flex items-center justify-between gap-3 py-1.5">
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-900">{s.stage ?? 'Stage sans titre'}</span>
                          <span className="block truncate text-xs text-gray-500">
                            {s.centre ?? 'Centre ?'} · {dateCourte(s.debut)} → {dateCourte(s.fin)}
                          </span>
                        </span>
                        <span className={clsx(
                          'shrink-0 tabular-nums text-xs',
                          t !== null && t >= 1 ? 'font-semibold text-red-700'
                            : t !== null && t >= SEUIL_PRESQUE_COMPLETE ? 'font-semibold text-amber-700'
                              : 'text-gray-600',
                        )}>
                          {s.inscrits} / {s.capacite}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }}
        </Bloc>

        {/* --- Demandes à traiter -------------------------------------- */}
        <Bloc titre="Demandes à traiter" icone={<Inbox className="h-4 w-4" />} lecture={demandes}>
          {(d) => (
            <ul className="space-y-3 text-sm">
              <Demande to={CHEMINS.planification.annulations} nombre={d.annulationsEnAttente}
                       libelle="demandes d’annulation en attente"
                       detail={d.annulationPlusAncienne ? `la plus ancienne du ${dateCourte(d.annulationPlusAncienne)}` : null} />
              <Demande to={CHEMINS.planification.inclusion} nombre={d.inclusionsSurSessionsAVenir}
                       libelle="demandes d’inclusion en attente sur des sessions à venir"
                       detail={`${d.inclusionsEnAttente} en attente au total, sessions passées comprises`} />
              <Demande to={`${CHEMINS.planification.listeAttente}?quand=a-venir`} nombre={d.listeAttenteSurSessionsAVenir}
                       libelle="enfants en liste d’attente sur des sessions à venir"
                       detail="en attente ou invités" />
            </ul>
          )}
        </Bloc>

        {/* --- Signal financier ---------------------------------------- */}
        <Bloc
          titre="Banque"
          icone={<Landmark className="h-4 w-4" />}
          lecture={dernierImport}
          lien={{ to: CHEMINS.comptabilite.rapprochement, libelle: 'Rapprochement' }}
        >
          {(imp) => (
            <div className="grid grid-cols-2 gap-3">
              <Chiffre
                valeur={imp ? dateCourte(imp.creeLe) : '—'}
                libelle={imp
                  ? `dernier import${imp.periodeFin ? `, mouvements jusqu’au ${dateCourte(imp.periodeFin)}` : imp.dateReleve ? `, relevé du ${dateCourte(imp.dateReleve)}` : ''}`
                  : 'aucun import'}
              />
              <Chiffre
                valeur={aTraiter.etat === 'pret' ? aTraiter.valeur : aTraiter.etat === 'erreur' ? '?' : '…'}
                libelle={aTraiter.etat === 'erreur' ? aTraiter.message : 'mouvements à imputer'}
                ton={aTraiter.etat === 'pret' && aTraiter.valeur > 0 ? 'attention' : 'neutre'}
              />
            </div>
          )}
        </Bloc>

        <Bloc
          titre="Derniers paiements reçus"
          icone={<Banknote className="h-4 w-4" />}
          lecture={paiements}
          lien={{ to: CHEMINS.comptabilite.accueil, libelle: 'Comptabilité' }}
        >
          {(liste) =>
            liste.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun paiement.</p>
            ) : (
              <ul className="divide-y divide-gray-100 text-sm">
                {liste.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="min-w-0">
                      <span className="block truncate text-gray-900">{p.contrepartie ?? 'Contrepartie inconnue'}</span>
                      <span className="block text-xs text-gray-500">
                        {dateCourte(p.dateValeur)} · {SOURCES[p.source] ?? p.source}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums font-medium">{euros(p.montantCents)}</span>
                  </li>
                ))}
              </ul>
            )
          }
        </Bloc>
      </div>
    </div>
  );
}

function Demande({ to, nombre, libelle, detail }: { to: string; nombre: number; libelle: string; detail: string | null }) {
  return (
    <li>
      <Link to={to} className="flex items-baseline gap-3 rounded-lg px-2 py-1 hover:bg-gray-50">
        <span className={clsx('w-12 shrink-0 text-right text-xl font-bold tabular-nums', nombre > 0 ? 'text-amber-700' : 'text-gray-400')}>
          {nombre}
        </span>
        <span>
          <span className="text-gray-900">{libelle}</span>
          {detail && <span className="block text-xs text-gray-500">{detail}</span>}
        </span>
      </Link>
    </li>
  );
}
